import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Building2, 
  Users, 
  Sparkles, 
  Wrench, 
  Calendar,
  ArrowRight,
  Clock,
  DollarSign
} from 'lucide-react';
import { getDashboardStats } from '@/lib/api';
import { format, parseISO } from 'date-fns';

export const DashboardHome = ({ 
  selectedProperty, 
  properties, 
  onNavigate 
}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats(selectedProperty?.id);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedProperty]);

  const statCards = [
    {
      title: 'Properties',
      value: stats?.total_properties || 0,
      icon: Building2,
      color: 'bg-blue-50 text-blue-600',
      path: '/properties'
    },
    {
      title: 'Bookings',
      value: stats?.total_bookings || 0,
      icon: Users,
      color: 'bg-emerald-50 text-emerald-600',
      path: '/bookings'
    },
    {
      title: 'Pending Cleaning',
      value: stats?.pending_cleaning || 0,
      icon: Sparkles,
      color: 'bg-green-50 text-green-600',
      path: '/cleaning'
    },
    {
      title: 'Pending Maintenance',
      value: stats?.pending_maintenance || 0,
      icon: Wrench,
      color: 'bg-amber-50 text-amber-600',
      path: '/maintenance'
    },
    {
      title: 'Expenses (This Month)',
      value: `$${(stats?.total_expenses_this_month || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: DollarSign,
      color: 'bg-purple-50 text-purple-600',
      path: '/expenses',
      isText: true
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            {selectedProperty ? selectedProperty.name : 'All Properties'}
          </h1>
          <p className="text-slate-500 mt-1">
            Overview of your vacation rental management
          </p>
        </div>
        <Button 
          onClick={() => onNavigate('/calendar')}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="view-calendar-btn"
        >
          <Calendar className="h-4 w-4 mr-2" />
          View Calendar
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.title}
              className="bg-white border border-slate-100 shadow-sm card-hover cursor-pointer"
              onClick={() => onNavigate(stat.path)}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                    <p className={`${stat.isText ? 'text-2xl' : 'text-3xl'} font-bold text-slate-900 mt-1 font-manrope`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Upcoming Events */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Check-ins */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-lg font-manrope">
              <span className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                Upcoming Check-ins
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onNavigate('/bookings')}
                data-testid="view-all-bookings-btn"
              >
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming_bookings?.length > 0 ? (
              stats.upcoming_bookings.slice(0, 5).map((booking) => (
                <div 
                  key={booking.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{booking.guest_name || 'Guest'}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(parseISO(booking.check_in), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <span className="badge-booking px-2 py-1 rounded text-xs font-medium">
                    Check-in
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No upcoming check-ins
              </p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Cleaning */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-lg font-manrope">
              <span className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                Cleaning Tasks
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onNavigate('/cleaning')}
                data-testid="view-all-cleaning-btn"
              >
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming_cleaning?.length > 0 ? (
              stats.upcoming_cleaning.slice(0, 5).map((task) => (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">
                      {task.notes || 'Cleaning scheduled'}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(parseISO(task.scheduled_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <span className="badge-cleaning px-2 py-1 rounded text-xs font-medium">
                    Pending
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No upcoming cleaning tasks
              </p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Maintenance */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-lg font-manrope">
              <span className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                Maintenance Tasks
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onNavigate('/maintenance')}
                data-testid="view-all-maintenance-btn"
              >
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming_maintenance?.length > 0 ? (
              stats.upcoming_maintenance.slice(0, 5).map((task) => (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{task.title}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(parseISO(task.scheduled_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium priority-${task.priority}`}>
                    {task.priority}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No upcoming maintenance
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
